import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { format, parseISO, startOfYear, endOfYear } from 'date-fns';
import { getScenarios } from './scenarios';
import { formatCurrency, formatPercent } from './formatters';
import type { ScenarioResult } from '../../../types';

function getYearlyData(results: ScenarioResult) {
  const yearlyData = [];
  const years = new Set(results.dates.map(date => parseISO(date).getFullYear()));
  
  for (const year of years) {
    const startDate = startOfYear(new Date(year, 0, 1));
    const endDate = endOfYear(new Date(year, 0, 1));
    
    // Find indices for start and end of year
    const startIdx = results.dates.findIndex(date => parseISO(date) >= startDate);
    const endIdx = results.dates.findIndex(date => parseISO(date) > endDate);
    const lastIdx = endIdx === -1 ? results.dates.length - 1 : endIdx - 1;
    
    if (startIdx === -1) continue;

    // Calculate yearly returns
    const getYearlyReturn = (values: number[]) => {
      const startValue = values[startIdx];
      const endValue = values[lastIdx];
      return (endValue - startValue) / startValue;
    };

    yearlyData.push({
      year: year.toString(),
      conservative: {
        value: results.risk_free[lastIdx],
        return: getYearlyReturn(results.risk_free)
      },
      veryLowRisk: {
        value: results.low_risk[lastIdx],
        return: getYearlyReturn(results.low_risk)
      },
      moderate: {
        value: results.moderate_risk[lastIdx],
        return: getYearlyReturn(results.moderate_risk)
      },
      higher: {
        value: results.high_risk[lastIdx],
        return: getYearlyReturn(results.high_risk)
      }
    });
  }

  return yearlyData;
}

export async function generatePDF(chartRef: any, results: ScenarioResult) {
  if (!chartRef.current) return;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  let yPosition = margin;

  // Helper function for text wrapping
  const splitText = (text: string, fontSize: number, maxWidth: number) => {
    doc.setFontSize(fontSize);
    return doc.splitTextToSize(text, maxWidth);
  };

  // Title and Date
  doc.setFontSize(24);
  doc.setTextColor(0, 0, 0);
  doc.text('HOA Investment Analysis Report', margin, yPosition);
  
  yPosition += 15;
  doc.setFontSize(12);
  doc.setTextColor(100, 100, 100);
  doc.text(`Generated on ${format(new Date(), 'MMMM d, yyyy')}`, margin, yPosition);
  
  // Executive Summary
  yPosition += 20;
  doc.setFontSize(16);
  doc.setTextColor(0, 0, 0);
  doc.text('Executive Summary', margin, yPosition);
  
  yPosition += 10;
  doc.setFontSize(11);
  doc.setTextColor(60, 60, 60);
  const summaryText = splitText(
    'This report demonstrates the potential returns available from different investment approaches for your HOA reserve fund. Each investment strategy is customized for the needs of your community, providing for the liquidity needs forecast by your Reserve Study while incorporating your capital expenses and risk profile. Most communities choose a very low risk or moderate risk strategy.',
    11,
    pageWidth - (margin * 2)
  );
  doc.text(summaryText, margin, yPosition);

  // Growth Projection Chart
  yPosition += 30;
  doc.setFontSize(16);
  doc.setTextColor(0, 0, 0);
  doc.text('Reserve Fund Growth Projection', margin, yPosition);

  yPosition += 10;
  const chartImage = chartRef.current.canvas.toDataURL('image/png');
  const chartWidth = pageWidth - (margin * 2);
  const chartHeight = 90;
  doc.addImage(chartImage, 'PNG', margin, yPosition, chartWidth, chartHeight);

  // Start a new page for Projected Reserve Fund Balances
  doc.addPage();
  yPosition = margin;

  // Strategy Results Table
  doc.setFontSize(16);
  doc.setTextColor(0, 0, 0);
  doc.text('Projected Reserve Fund Balances', margin, yPosition);

  yPosition += 10;
  const scenarios = getScenarios(results);
  const tableData = scenarios.map(scenario => [
    scenario.name,
    formatCurrency(scenario.value),
    formatPercent(scenario.metrics.totalReturn),
    formatPercent(scenario.metrics.annualizedReturn)
  ]);

  (doc as any).autoTable({
    startY: yPosition,
    head: [['Investment Strategy', 'End Balance', 'Total Return', 'Annual Return']],
    body: tableData,
    theme: 'striped',
    headStyles: { 
      fillColor: [37, 99, 235],
      textColor: 255,
      fontSize: 11,
      fontStyle: 'bold'
    },
    styles: {
      fontSize: 10,
      cellPadding: 3
    },
    columnStyles: {
      0: { cellWidth: 50 },
      1: { cellWidth: 45 },
      2: { cellWidth: 40 },
      3: { cellWidth: 40 }
    }
  });

  // Yearly Breakdown Table
  yPosition = (doc as any).lastAutoTable.finalY + 20;
  doc.setFontSize(14);
  doc.setTextColor(0, 0, 0);
  doc.text('Annual Performance Breakdown', margin, yPosition);

  yPosition += 10;
  const yearlyData = getYearlyData(results);
  const yearlyTableData = yearlyData.map(year => [
    year.year,
    formatPercent(year.conservative.return),
    formatCurrency(year.conservative.value),
    formatPercent(year.veryLowRisk.return),
    formatCurrency(year.veryLowRisk.value),
    formatPercent(year.moderate.return),
    formatCurrency(year.moderate.value),
    formatPercent(year.higher.return),
    formatCurrency(year.higher.value)
  ]);

  (doc as any).autoTable({
    startY: yPosition,
    head: [
      ['Year', 'Return %', 'Conservative', 'Return %', 'Very Low Risk', 'Return %', 'Moderate', 'Return %', 'Higher']
    ],
    body: yearlyTableData,
    theme: 'striped',
    headStyles: {
      fillColor: [37, 99, 235],
      textColor: 255,
      fontSize: 9,
      fontStyle: 'bold'
    },
    styles: {
      fontSize: 8,
      cellPadding: 2
    },
    columnStyles: {
      0: { cellWidth: 15 },
      1: { cellWidth: 20 },
      2: { cellWidth: 25 },
      3: { cellWidth: 20 },
      4: { cellWidth: 25 },
      5: { cellWidth: 20 },
      6: { cellWidth: 25 },
      7: { cellWidth: 20 },
      8: { cellWidth: 25 }
    }
  });

  // Investment Strategies
  doc.addPage();
  yPosition = margin;
  
  doc.setFontSize(16);
  doc.setTextColor(0, 0, 0);
  doc.text('Investment Strategy Details', margin, yPosition);

  yPosition += 10;
  doc.setFontSize(11);
  doc.setTextColor(60, 60, 60);
  const introText = splitText(
    'Each investment strategy will be customized for the needs of the community. Your portfolio will provide for the liquidity (cash) needs forecast by your Reserve Study and incorporate your capital expenses and risk profile. Most communities choose a very low risk or moderate risk strategy.',
    11,
    pageWidth - (margin * 2)
  );
  doc.text(introText, margin, yPosition);

  const strategies = [
    {
      name: 'Conservative Strategy',
      description: 'A conservative cash-based strategy that earns a variable interest rate between 2.5 - 4% annually'
    },
    {
      name: 'Low Risk Strategy',
      description: 'A low risk strategy that earns an average annual return forecasted from laddered government securities and treasuries'
    },
    {
      name: 'Moderate Risk Strategy',
      description: 'A moderate risk strategy that allocates 60% to government securities and treasuries and 40% to highly rated corporate bonds'
    },
    {
      name: 'Higher Risk Strategy',
      description: 'A higher risk strategy is not recommended for most communities. This strategy would allocate 60% to government securities and treasuries, 30% to highly rated corporate bonds and 10% to SPY'
    }
  ];

  yPosition += 20;
  strategies.forEach((strategy) => {
    if (yPosition > pageHeight - 40) {
      doc.addPage();
      yPosition = margin;
    }

    doc.setFontSize(13);
    doc.setTextColor(0, 0, 0);
    doc.text(strategy.name, margin, yPosition);

    yPosition += 7;
    doc.setFontSize(11);
    doc.setTextColor(60, 60, 60);
    const descText = splitText(strategy.description, 11, pageWidth - (margin * 2));
    doc.text(descText, margin, yPosition);

    yPosition += (descText.length * 5) + 8;
  });

  // Disclaimer
  if (yPosition > pageHeight - 60) {
    doc.addPage();
    yPosition = margin;
  }

  yPosition += 10;
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  const disclaimer = splitText(
    'Disclaimer: This report provides projections based on historical data and current market conditions. The analysis emphasizes potential returns from different investment approaches but actual results may vary. Past performance is not indicative of future results. Please consult with a financial advisor before making investment decisions.',
    10,
    pageWidth - (margin * 2)
  );
  doc.text(disclaimer, margin, yPosition);

  doc.save('hoa-investment-analysis.pdf');
}